<template>
    <!--
      채점 탭 — 문항 하나의 평가요소 목록.
      백엔드 모델 그대로다: 평가요소(df_evaluations_norm) 여러 개가 문항에 순서대로 붙고(subs_init_evaluations),
      평가요소마다 수식·점수·중요도·충족/미충족 수준 코드·증빙 위치(ref_path)를 갖는다.
      저장 계약은 SubEvaluationHandler 의 ev/evn/s2e 컬럼 그대로다. 여기서는 그 배열을 만들 뿐이다.
    -->
    <div class="score">

        <!-- 문항 단위 배점 — 평가요소가 아니라 문항의 속성 -->
        <div class="meta">
            <div class="mrow">
                <label>계산 방법 <code class="mono">calc_method</code></label>
                <select v-model="meta.calc_method">
                    <option :value="null">(없음 — 채점 안 함)</option>
                    <option value="DIVIDE">DIVIDE — 배점 × 충족수 ÷ 평가요소수</option>
                    <option value="POSITIVE">POSITIVE — 같은 산식</option>
                    <option value="NEGATIVE">NEGATIVE — 같은 산식</option>
                    <option value="SUM">SUM — min(평가요소 점수 합, 배점)</option>
                    <option value="EXCEPTION">EXCEPTION — 백엔드 하드코딩</option>
                </select>
            </div>
            <div class="mrow two">
                <span><label>배점 <code class="mono">cap_score</code></label><input v-model.number="meta.cap_score" type="number" step="0.5" min="0"></span>
                <span><label>배율 <code class="mono">score_scaler</code></label><input v-model.number="meta.score_scaler" type="number" step="0.1" min="0"></span>
            </div>
            <p class="h">{{ metaSay }}</p>
        </div>

        <!-- 평가요소 목록 -->
        <p class="group-hd">평가요소 <span class="cnt">{{ criteria.length }}</span></p>
        <p v-if="!criteria.length" class="h">아직 평가요소가 없습니다. 「+ 평가요소」을 누르세요.</p>

        <div v-for="(c, i) in criteria" :key="c.id" class="crit" :class="{active: c.id === activeId}" :style="colorStyle(i + 1)">
            <!-- 접힌 머리: 항상 보임 -->
            <div class="chd" @click="toggle(c.id)">
                <span class="cn">{{ i + 1 }}</span>
                <span class="cname">{{ c.name || '(충족 문구 없음)' }}</span>
                <span class="chips">
                    <span class="chip" :class="{zero: Number(c.score) === 0}" :title="Number(c.score) === 0 ? '0점 — 부정 근거' : '평가요소 점수(가중치)'">
                        {{ c.score == null || c.score === '' ? '점수 없음' : c.score + '점' }}
                    </span>
                    <span class="chip imp" :class="c.importance">{{ c.importance }}</span>
                    <span v-if="c.pos_ev_code || c.neg_ev_code" class="chip">{{ c.pos_ev_code || '–' }} / {{ c.neg_ev_code || '–' }}</span>
                    <span v-if="c.ref_path" class="chip clip" title="증빙 위치"><AppIcon name="paperclip" class="mr-1"/>{{ refSay(c.ref_path) }}</span>
                    <span v-else-if="c.ignore_attach" class="chip muted">첨부 무시</span>
                </span>
                <span class="mv">
                    <button :disabled="i === 0" title="위로" @click.stop="move(i, -1)">↑</button>
                    <button :disabled="i === criteria.length - 1" title="아래로" @click.stop="move(i, 1)">↓</button>
                </span>
            </div>
            <div class="csay" v-html="sentenceHtml(c)"></div>

            <!-- 펼친 본문: 활성 평가요소만. 왼쪽 = 문구와 통과 조건(주 작업), 오른쪽 = 증빙·점수·중요도·수준 -->
            <div v-if="c.id === activeId" class="cbd">
              <div class="col-main">
                <div class="f two">
                    <span><label>충족</label><input v-model="c.name" type="text" placeholder="예) 근로시간을 관리함"></span>
                    <span><label>미충족</label><input v-model="c.reverse_name" type="text" placeholder="예) 관리하지 않음"></span>
                </div>

                <!-- 통과 조건 — 구조를 클릭해서 만드는 것이 주 경로다 -->
                <div class="f">
                    <label>통과 조건
                        <span v-if="c.raw" class="badge" :class="readOf(c).ok ? 'gen' : (readOf(c).reason === 'manual' ? 'manual' : 'warn')">
                            {{ readOf(c).ok ? '저장된 수식' : readOf(c).reason === 'always-true' ? '항상 참' : readOf(c).reason === 'not-a-formula' ? '수식 아님' : '직접 작성' }}
                        </span>
                        <span v-else-if="c.ev_id != null" class="badge gen" title="블럭 표기로 저장된 수식이라 블럭으로 되돌렸습니다">블럭</span>
                        <button v-if="!c.raw" class="btn tiny toraw" title="블럭이 표현하지 못하는 수식을 직접 쓰려면"
                                @click="toRaw(c)">직접 편집</button>
                        <span v-else class="hint">← 왼쪽 구조에서 대상을 클릭해 조건을 만듭니다</span>
                    </label>
                    <template v-if="c.raw">
                        <div class="rawbox">
                            <div class="rawsay" v-html="sentenceHtml(c)"></div>
                            <div class="rawacts">
                                <button class="btn tiny primary" @click="toBlocks(c)">블럭으로 만들기</button>
                                <span class="h" style="margin:0">저장된 수식은 블럭으로 되돌릴 수 없어 새로 만듭니다 — 기존 수식은 버려집니다.</span>
                            </div>
                            <details class="rawdet">
                                <summary>수식 문자열 직접 고치기</summary>
                                <textarea v-model="c.formula" class="rawf mono" rows="3" spellcheck="false"></textarea>
                            </details>
                        </div>
                    </template>
                    <template v-else>
                        <sv2-cond :node="c.cond" :targets="targets" :active="condActive" :picking="isPicking('target')"
                                  :is-root="true" :prefix="(i + 1) + '.'" :color="colorOf(i + 1)"
                                  @focus="condActive = $event" @pick="startTarget(c, $event)" @set="condSet(c, $event)"
                                  @join="condJoin(c, $event)" @add-cond="condAdd(c, $event)" @add-group="condAddGroup(c, $event)" @del="condDel(c, $event)" />
                        <p class="rule">한 단계에서는 <b>그리고</b>·<b>또는</b> 중 하나만. 섞으려면 <b>묶음</b>.</p>
                        <pre class="code mono">{{ builtOf(c) ? builtOf(c).py : '# 대상을 고르면 수식이 만들어집니다' }}</pre>
                    </template>
                </div>
              </div>

              <div class="col-side">
                <!-- 증빙 위치 — 수식 대상과 다른 축. '이 평가요소는 어디에 파일이 있어야 하는가' -->
                <div class="f">
                    <label>증빙 위치 <code class="mono">ref_path</code></label>
                    <div class="evid" :class="{picking: isPicking('evidence') && pick.critId === c.id, set: !!c.ref_path}"
                         @click="startEvidence(c)">
                        <template v-if="c.ref_path">
                            <AppIcon name="paperclip" class="clipsw"/>
                            <span>{{ refSay(c.ref_path) }}</span>
                            <code class="mono where">{{ c.ref_path }}</code>
                            <button class="x" title="증빙 위치 지우기" @click.stop="clearEvidence(c)">×</button>
                        </template>
                        <template v-else-if="isPicking('evidence') && pick.critId === c.id">
                            왼쪽 구조에서 <b>선택지 · 요소 · 표 행</b>을 클릭하세요 →
                        </template>
                        <template v-else>구조에서 파일이 붙어야 할 자리 고르기 →</template>
                    </div>
                    <p class="h">지정하면 채점 때 <b>그 자리에 첨부가 없으면 미충족</b>으로 뒤집힙니다. 문항 전체 첨부로 판정하려면 비워 둡니다.</p>
                    <label class="cb"><input v-model="c.ignore_attach" type="checkbox"><span>첨부와 무관하게 판정 (ignore_attach)</span></label>
                </div>

                <!-- 점수 · 중요도 · 수준 -->
                <div class="f stack">
                    <span><label>점수(가중치)</label><input v-model.number="c.score" type="number" step="0.5" placeholder="비움">
                        <em v-if="Number(c.score) === 0" class="h zero">0점 — 부정 근거. 맞아도 점수를 주지 않습니다.</em></span>
                    <span><label>중요도</label>
                        <select v-model="c.importance"><option v-for="o in IMPORTANCE" :key="o" :value="o">{{ o }}</option></select></span>
                    <span><label>수준 (충족 / 미충족)</label>
                        <span class="lvl">
                            <select v-model="c.pos_ev_code"><option :value="null">–</option><option v-for="e in evCodes" :key="e.code" :value="e.code">{{ e.code }} {{ e.description }} ({{ e.level }})</option></select>
                            <span class="slash">/</span>
                            <select v-model="c.neg_ev_code"><option :value="null">–</option><option v-for="e in evCodes" :key="e.code" :value="e.code">{{ e.code }} {{ e.description }} ({{ e.level }})</option></select>
                        </span></span>
                </div>
                <p class="h">중요도는 점수 배율이 아닙니다 — HIGH 평가요소가 있는 문항만 따로 골라 full/half/empty 등급을 냅니다.
                    수준 코드는 충족이면 앞, 미충족이면 뒤 코드의 <code class="mono">level</code> 이 됩니다.</p>

                <!-- 부가 정보 — 백엔드 evn_columns 에 이미 있는 항목들. 전에는 저장만 되고 편집이 안 됐다. -->
                <details class="extra">
                    <summary>부가 정보 <span v-if="extraCount(c)" class="badge">{{ extraCount(c) }}</span></summary>

                    <div class="f">
                        <label>충족·미충족 문구 번역</label>
                        <div class="tr">
                            <span class="lb">충족</span>
                            <input v-model="c.name_ea" type="text" placeholder="English">
                            <input v-model="c.name_ca" type="text" placeholder="中文">
                        </div>
                        <div class="tr">
                            <span class="lb">미충족</span>
                            <input v-model="c.reverse_name_ea" type="text" placeholder="English">
                            <input v-model="c.reverse_name_ca" type="text" placeholder="中文">
                        </div>
                        <p class="h">국문만 고치면 번역이 옛 문장으로 남습니다. 함께 손봐 주세요.</p>
                    </div>

                    <div class="f">
                        <label class="cb"><input v-model="c.is_ap" type="checkbox"><span>액션플랜 적용</span></label>
                        <div v-if="c.is_ap" class="sub">
                            <textarea v-model="c.ap_description" rows="2" placeholder="(국문) 액션 플랜 설명 — 필수"></textarea>
                            <input v-model="c.ap_description_ea" type="text" placeholder="(영문) 액션 플랜 설명">
                            <input v-model="c.ap_description_ca" type="text" placeholder="(중문) 액션 플랜 설명">
                        </div>
                    </div>

                    <div class="f stack">
                        <span><label>리스크 등급</label>
                            <select v-model="c.risk_lv">
                                <option v-for="r in RISK_LEVELS" :key="String(r.v)" :value="r.v">{{ r.ko }}</option>
                            </select></span>
                        <span><label>KESG 식별자</label><input v-model="c.kesg_id" type="text" placeholder="비움"></span>
                    </div>
                    <label class="cb"><input v-model="c.is_compliance" type="checkbox"><span>컴플라이언스 적용</span></label>
                </details>

                <div class="acts">
                    <button class="btn tiny bad" @click="remove(c.id)">이 평가요소 삭제</button>
                </div>
              </div>
            </div>
        </div>

        <div class="addrow">
            <button class="addc" @click="add">+ 평가요소</button>
            <button v-if="bulkChoices.length" class="addc bulk" @click="addPerChoice">
                선택지마다 만들기 <span class="n">{{ bulkChoices.length }}</span>
            </button>
        </div>

        <!-- 저장 -->
        <details class="payload">
            <summary>저장될 형태 — <code class="mono">POST /api/evaluation/{{ subCode }}</code> 의 evaluations 배열</summary>
            <pre class="code mono">{{ payloadPreview }}</pre>
        </details>
        <ul v-if="problems.length" class="probs">
            <li v-for="(p, i) in problems" :key="i">
                <button class="lnk" @click="activeId = p.id">{{ p.at }}번 평가요소</button> — {{ p.msg }}
            </li>
        </ul>
        <div class="save">
            <span v-if="savedAt" class="h" style="margin:0">{{ savedAt }}</span>
            <span v-if="!criteria.length" class="h" style="margin:0">평가요소가 하나도 없으면 저장할 수 없습니다.</span>
            <button class="btn tiny primary" :disabled="busy || !criteria.length" @click="save">{{ busy ? '저장 중…' : '평가요소 저장' }}</button>
        </div>
    </div>
</template>

<script>
import Sv2Cond from './Sv2Cond.vue';
import {
    OPS, buildFormula, readFormula, sentenceOf, numberConds, allConds, findParent, newCond, newGroup,
    colorOf, IMPORTANCE, newCriterion, criterionFromApi, criterionToApi, formulaToCond, RISK_LEVELS
} from '@core_utils/subComponent/scoring-formula.js';

const OKLCH = typeof CSS !== 'undefined' && CSS.supports && CSS.supports('color', 'oklch(0.5 0.1 0)');
const esc = (t) => String(t == null ? '' : t).replace(/[&<>"]/g, (ch) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'}[ch]));
const CALC_SAY = {
    DIVIDE: '배점 × (충족한 평가요소 수 ÷ 평가요소 수)', POSITIVE: '배점 × (충족한 평가요소 수 ÷ 평가요소 수)', NEGATIVE: '배점 × (충족한 평가요소 수 ÷ 평가요소 수)',
    SUM: '평가요소 점수를 더해 배점을 넘지 않게', EXCEPTION: '백엔드에 문항 코드별로 박힌 규칙'
};

export default {
    name: 'Sv2Score',
    components: {Sv2Cond},
    props: {
        root: {type: Object, default: null},
        targets: {type: Array, default: () => []},
        refPaths: {type: Array, default: () => []},
        initial: {type: Array, default: () => []},          // API 에서 온 기준 배열
        subMeta: {type: Object, default: () => ({})},       // {calc_method, cap_score, score_scaler}
        evCodes: {type: Array, default: () => []},
        subCode: {type: String, default: ''},
        busy: {type: Boolean, default: false}               // 부모(Sv2Editor)가 저장 중이면 저장 버튼을 잠근다
    },
    data() {
        return {
            criteria: [], activeId: null, condActive: null,
            pick: null,                 // {kind:'target'|'evidence', critId, condId}
            seq: 0, savedAt: '', baseline: '', RISK_LEVELS,
            meta: {calc_method: null, cap_score: null, score_scaler: 1},
            IMPORTANCE
        };
    },
    computed: {
        metaSay() {
            const m = this.meta;
            if (!m.calc_method) return '계산 방법이 없으면 이 문항은 점수를 내지 않습니다 (충족/미충족만).';
            return `${CALC_SAY[m.calc_method] || m.calc_method}${m.cap_score != null ? ` · 배점 ${m.cap_score}` : ''}${m.score_scaler && m.score_scaler !== 1 ? ` × ${m.score_scaler}` : ''}`;
        },
        /** 체크박스 문항에서만 — 선택지마다 '골랐으면' 평가요소를 한 번에 만든다 */
        bulkChoices() {
            const t = this.targets.find((x) => x.kind === 'multi' && x.path === 'result');
            return t && t.choices ? t.choices : [];
        },
        payload() { return this.criteria.map((c, i) => criterionToApi(c, this.targets, i + 1)); },
        /** 저장 전 필수값 — v1 handleSave 와 같은 규칙. 막지 않으면 빈 수식이 그대로 저장된다. */
        problems() {
            const out = [];
            this.criteria.forEach((c, i) => {
                const at = i + 1;
                const p = this.payload[i] || {};
                if (!p.formula) out.push({id: c.id, at, msg: '통과 조건이 비어 있습니다. 대상을 고르거나 수식을 적어 주세요.'});
                if (!c.name && !c.reverse_name) out.push({id: c.id, at, msg: '충족·미충족 문구 중 하나는 있어야 합니다.'});
                if (c.is_ap && !c.ap_description) out.push({id: c.id, at, msg: '액션플랜을 켜면 국문 설명이 필요합니다.'});
            });
            return out;
        },
        /** 마지막으로 불러온(또는 저장한) 상태와 다른가. baseline 은 initial/targets/subMeta 가 바뀔 때 다시 찍는다. */
        snapshot() { return JSON.stringify([this.payload, this.meta]); },
        isDirty() { return this.baseline !== '' && this.snapshot !== this.baseline; },
        payloadPreview() { return JSON.stringify(this.payload, null, 2).slice(0, 4000); },
        /** 구조 격자에 그릴 표시: 증빙은 모든 평가요소, 수식 대상은 활성 평가요소만 */
        marks() {
            const out = {};
            const put = (id, m) => { (out[id] = out[id] || []).push(m); };
            this.criteria.forEach((c, i) => {
                const k = colorOf(i + 1, OKLCH);
                if (c.refNodeId) put(c.refNodeId, {label: String(i + 1), kind: 'evidence', c: k.c, ct: k.ct});
                if (c.id === this.activeId && !c.raw && c.cond) {
                    numberConds(c.cond);
                    allConds(c.cond).forEach((cd) => { if (cd.target) put(cd.target, {label: `${i + 1}.${cd.n}`, kind: 'target', c: k.c, ct: k.ct}); });
                }
            });
            return out;
        }
    },
    watch: {
        initial: {immediate: true, handler(list) {
            // 블럭 표기로 저장된 수식은 블럭으로 돌아온다. targets 가 아직 없으면(문항 로드 전) 문자열로.
            this.criteria = (list || []).map((ev) => criterionFromApi(ev, `c${++this.seq}`, this.refPaths, this.targets));
            this.activeId = null; this.pick = null; this.savedAt = '';
            this.$nextTick(() => { this.baseline = this.snapshot; });
        }},
        isDirty(v) { this.$emit('dirty', v); },
        targets(ts) {
            if (!ts.length) return;
            this.criteria.forEach((c) => {
                if (!c.raw || !c._src) return;
                const back = formulaToCond(c.formula, ts, `${c.id}k`);
                if (back.ok) { this.$set(c, 'cond', back.cond); this.$set(c, 'raw', false); }
            });
            // 역해석은 사용자의 편집이 아니다 — 기준선을 다시 찍는다
            this.$nextTick(() => { this.baseline = this.snapshot; });
        },
        subMeta: {immediate: true, handler(m) {
            this.meta = {calc_method: m && m.calc_method != null ? m.calc_method : null,
                cap_score: m && m.cap_score != null ? m.cap_score : null,
                score_scaler: m && m.score_scaler != null ? m.score_scaler : 1};
            this.$nextTick(() => { this.baseline = this.snapshot; });
        }},
        marks: {immediate: true, handler(v) { this.$emit('marks', v); }},
        pick(v) { this.$emit('pick-mode', v ? v.kind : null); }
    },
    methods: {
        colorOf(n) { return colorOf(n, OKLCH); },
        colorStyle(n) { const k = colorOf(n, OKLCH); return {'--c': k.c, '--ct': k.ct}; },
        isPicking(kind) { return !!this.pick && this.pick.kind === kind; },
        refSay(path) { const r = this.refPaths.find((x) => x.path === path); return r ? r.say : path; },
        readOf(c) { return readFormula(c.formula); },
        builtOf(c) { return c.cond ? buildFormula(c.cond, this.targets) : null; },
        crit(id) { return this.criteria.find((c) => c.id === id); },

        /** 접힌 줄에 보이는 문장 — raw 면 읽어서, 블럭이면 만들어서 */
        sentenceHtml(c) {
            const kw = (t) => `<span class="kw">${t}</span>`;
            if (c.raw) {
                const r = readFormula(c.formula);
                if (!r.ok) return `<span class="raw mono">${esc(c.formula) || '(수식 없음)'}</span>`;
                const html = (n) => n.type === 'leaf' ? esc(n.say)
                    : (n.wrapped ? `<span class="grp-say">${n.items.map(html).join(` ${kw(n.join === 'and' ? '그리고' : '또는')} `)}</span>`
                        : n.items.map(html).join(` ${kw(n.join === 'and' ? '그리고' : '또는')} `));
                return html(r.node) + ` → ${kw('충족')}`;
            }
            const b = this.builtOf(c);
            if (!b) return '<span class="muted">조건을 만들면 문장이 나옵니다.</span>';
            const say = (n) => {
                if (n.type === 'leaf') {
                    const v = n.target.kind === 'choice' || n.target.kind === 'multi' ? `「${esc(n.valueText)}」` : n.target.kind === 'number' ? esc(n.valueText) : '';
                    return `<span class="q">${esc(n.target.say)}</span>${n.target.kind === 'text' || n.target.kind === 'file' ? '' : '이 '}${v} ${kw(esc(n.op.say))}`;
                }
                const inner = n.items.map(say).join(` ${kw(n.join === 'and' ? '그리고' : '또는')} `);
                return n.wrapped ? `<span class="grp-say">${inner}</span>` : inner;
            };
            return say(b.tree) + ` → ${kw('충족')}`;
        },

        /* ── 평가요소 목록 ── */
        toggle(id) {
            this.activeId = this.activeId === id ? null : id; this.pick = null; this.condActive = null;
            if (this.activeId) this.$nextTick(() => { const el = this.$el.querySelector('.crit.active'); if (el && el.scrollIntoView) el.scrollIntoView({block: 'start', behavior: 'smooth'}); });
        },
        add() {
            const c = newCriterion(`c${++this.seq}`);
            this.criteria.push(c); this.activeId = c.id; this.pick = null;
            this.$nextTick(() => { const el = this.$el.querySelector('.crit.active'); if (el && el.scrollIntoView) el.scrollIntoView({block: 'start', behavior: 'smooth'}); });
        },
        ask(title, message, state = 'warning') { return this.$showModal({type: 'confirm', state, title, message}); },
        async remove(id) {
            const i = this.criteria.findIndex((c) => c.id === id);
            if (i < 0) return;
            const c = this.criteria[i];
            const ok = await this.ask('평가요소 삭제', `${i + 1}번 평가요소 '${c.name || '이름 없음'}'을 목록에서 뺍니다.` + (c.ev_id != null ? ' 저장하면 서버에서 삭제됩니다.' : ''));
            if (!ok) return;
            const j = this.criteria.findIndex((x) => x.id === id);
            if (j >= 0) this.criteria.splice(j, 1);
            if (this.activeId === id) this.activeId = null;
            this.pick = null;
        },
        move(i, d) { const j = i + d; if (j < 0 || j >= this.criteria.length) return; const a = this.criteria.splice(i, 1)[0]; this.criteria.splice(j, 0, a); },
        async toBlocks(c) {
            const ok = await this.ask('블럭으로 만들기', `현재 수식 '${c.formula || '(비어 있음)'}' 을 버리고 빈 블럭으로 새로 만듭니다. 되돌릴 수 없습니다.`);
            if (!ok) return;
            this.$set(c, 'raw', false); this.$set(c, 'cond', newGroup(`${c.id}g`, 'and')); this.$set(c, 'formula', '');
        },

        /* ── 구조 클릭 라우팅: 부모가 노드 id 로 부른다 ── */
        onPick(nodeId) {
            if (!this.pick) return false;
            const c = this.crit(this.pick.critId);
            if (!c) { this.pick = null; return false; }
            if (this.pick.kind === 'evidence') {
                const r = this.refPaths.find((x) => x.id === nodeId);
                if (!r) { this.$emit('flash', '증빙은 선택지·선택지 안 요소·표 행에 붙습니다. 그 자리를 클릭하세요.'); return true; }
                this.$set(c, 'ref_path', r.path); this.$set(c, 'refNodeId', r.id);
                this.pick = null; return true;
            }
            // target
            const t = this.targets.find((x) => x.id === nodeId);
            if (!t) { this.$emit('flash', '답이 저장되는 자리만 대상이 될 수 있습니다 (문항의 답, 표의 입력 칸, 선택지 안의 입력).'); return true; }
            let cd = this.pick.condId != null ? this.condById(c.cond, this.pick.condId) : null;
            if (!cd || cd.kind !== 'cond') { cd = newCond(`${c.id}k${++this.seq}`); c.cond.items.push(cd); }
            this.$set(cd, 'target', t.id); this.$set(cd, 'op', null);
            this.$set(cd, 'value', t.kind === 'choice' || t.kind === 'multi' ? (t.choices[0] ? t.choices[0].value : '') : t.kind === 'number' ? 10 : '');
            this.condActive = cd.id; this.pick = null; return true;
        },
        startEvidence(c) { this.activeId = c.id; this.pick = this.pick && this.pick.kind === 'evidence' && this.pick.critId === c.id ? null : {kind: 'evidence', critId: c.id}; },
        clearEvidence(c) { this.$set(c, 'ref_path', null); this.$set(c, 'refNodeId', null); if (this.isPicking('evidence')) this.pick = null; },
        startTarget(c, condId) { this.condActive = condId; this.pick = {kind: 'target', critId: c.id, condId}; },

        /* ── 조건 트리 조작 (활성 평가요소 안) ── */
        condById(g, id) { if (!g) return null; if (g.id === id) return g; for (const it of g.items) { if (it.id === id) return it; if (it.kind === 'group') { const h = this.condById(it, id); if (h) return h; } } return null; },
        condSet(c, {id, op, value}) { const cd = this.condById(c.cond, id); if (!cd) return; if (op !== undefined) this.$set(cd, 'op', op); if (value !== undefined) this.$set(cd, 'value', value); },
        condJoin(c, {id, join}) { const g = this.condById(c.cond, id); if (g) this.$set(g, 'join', join); },
        condAdd(c, gid) { const g = this.condById(c.cond, gid) || c.cond; const cd = newCond(`${c.id}k${++this.seq}`); g.items.push(cd); this.startTarget(c, cd.id); },
        condAddGroup(c, gid) {
            const g = this.condById(c.cond, gid) || c.cond;
            const ng = newGroup(`${c.id}g${++this.seq}`, g.join === 'and' ? 'or' : 'and');
            const cd = newCond(`${c.id}k${++this.seq}`); ng.items.push(cd); g.items.push(ng); this.startTarget(c, cd.id);
        },
        async condDel(c, id) {
            const n = this.condById(c.cond, id); if (!n) return;
            if (n.items && n.items.length && !(await this.ask('묶음 삭제', `묶음 안의 조건 ${n.items.length}개도 함께 지웁니다.`))) return;
            const p = findParent(c.cond, n); if (!p) return; p.items.splice(p.items.indexOf(n), 1); if (this.condActive === id) this.condActive = null; this.pick = null; },

        /** 블럭 → 손수식. 블럭이 표현 못 하는 식을 쓸 때. 되돌리기는 「블럭으로 만들기」. */
        toRaw(c) {
            const built = buildFormula(c.cond, this.targets);
            this.$set(c, 'formula', built ? built.py : '');
            this.$set(c, 'raw', true);
        },
        /** 선택지마다 평가요소 하나씩. 점수는 숫자 1 (v1 은 '+1' 문자열을 넣었다). */
        addPerChoice() {
            const t = this.targets.find((x) => x.kind === 'multi' && x.path === 'result');
            if (!t) return;
            let made = 0;
            (t.choices || []).forEach((ch) => {
                const val = ch.value == null ? ch.text : ch.value;
                if (val === '' || val == null) return;
                const c = newCriterion(`c${++this.seq}`);
                c.name = ch.text || String(val);
                c.score = 1;
                const cond = newCond(`${c.id}k${++this.seq}`);
                cond.target = t.id; cond.op = 'in'; cond.value = val;
                c.cond.items.push(cond);
                this.criteria.push(c);
                made++;
            });
            if (made) { this.activeId = this.criteria[this.criteria.length - 1].id; this.pick = null; }
            this.$emit('flash', `선택지 ${made}개로 평가요소를 만들었습니다. 문구와 점수를 확인하세요.`);
        },
        /** 채워진 부가 항목 수 — 접힌 채로도 뭔가 들어 있는지 보이게 */
        extraCount(c) {
            return [c.name_ea, c.name_ca, c.reverse_name_ea, c.reverse_name_ca,
                c.ap_description, c.is_ap, c.is_compliance, c.risk_lv, c.kesg_id].filter(Boolean).length;
        },
        async save() {
            if (this.problems.length) {
                // 실패를 삼키지 않는다 — 빈 수식이 저장되면 그 문항 채점이 조용히 어긋난다
                this.activeId = this.problems[0].id;
                this.$emit('flash', `평가요소 ${this.problems.length}곳을 먼저 채워 주세요.`);
                return;
            }
            // 목록에서 빠진 저장본 평가요소는 서버에서 물리 삭제된다
            const gone = this.initial.filter((ev) => ev.ev_id != null && !this.criteria.some((c) => c.ev_id === ev.ev_id));
            if (gone.length && !(await this.ask('평가요소 삭제 저장', `평가요소 ${gone.length}개가 서버에서 삭제됩니다: ${gone.map((ev) => ev.name || ev.ev_id).join(', ')}. 저장하면 되돌릴 수 없습니다.`, 'error'))) return;
            this.savedAt = '';
            this.$emit('save', {evaluations: this.payload, meta: {...this.meta}});
        }
    }
};
void OPS; void sentenceOf;
</script>

<style scoped>
.score{display:flex;flex-direction:column;gap:.7rem}
.mono{font-family:Consolas,monospace}
.btn.toraw{margin-left:.35rem;font-size:.7rem;padding:0 .4rem}
.addc.bulk{margin-left:.35rem}
.addc.bulk .n{font-family:Consolas,monospace;font-size:.7rem;color:#fff;background:#2f6b8f;border-radius:999px;padding:0 .4rem;margin-left:.25rem}
.extra{margin:.5rem 0 0;border-top:1px solid #eaeef3;padding-top:.5rem}
.extra summary{font-size:.78rem;color:#6b747e;cursor:pointer}
.extra .badge{font-family:Consolas,monospace;font-size:.66rem;color:#fff;background:#2f6b8f;border-radius:999px;padding:0 .4rem;margin-left:.25rem}
.extra .sub{margin:.35rem 0 0 1.5rem;display:flex;flex-direction:column;gap:.25rem}
.extra .tr{display:flex;gap:.3rem;align-items:center;margin-bottom:.25rem}
.extra .tr .lb{flex:none;width:3.2rem;font-size:.78rem;color:#6b747e}
.extra .tr input{flex:1;min-width:0}
.probs{margin:.4rem 0 0;padding:.5rem .7rem .5rem 1.6rem;background:#fbeceb;border:1px solid #e3b7b2;border-radius:6px;
  font-size:.82rem;color:#9e2f24}
.probs li{margin:.1rem 0}
.probs .lnk{border:0;background:none;padding:0;font:inherit;color:#9e2f24;font-weight:700;text-decoration:underline;cursor:pointer}
.h{font-size:.76rem;color:#6b747e;margin:.2rem 0 0;line-height:1.5}
.group-hd{font-size:.8rem;font-weight:700;color:#141921;margin:.5rem 0 0;display:flex;align-items:center;gap:.4rem}
.group-hd .cnt{font-family:Consolas,monospace;font-size:.7rem;color:#fff;background:#2f6b8f;border-radius:999px;padding:0 .45rem}

/* 문항 배점 */
.meta{background:#f8f9fb;border:1px solid #eaeef3;border-radius:7px;padding:.6rem .75rem;display:flex;flex-direction:column;gap:.4rem}
.mrow{display:flex;flex-direction:column;gap:.2rem}
.mrow.two{flex-direction:row;gap:.6rem} .mrow.two>span{flex:1;display:flex;flex-direction:column;gap:.2rem}
.mrow label{font-size:.72rem;color:#6b747e;font-weight:500}
.mrow select,.mrow input{border:1px solid #dde2e9;border-radius:5px;padding:.28rem .5rem;font:inherit;font-size:.84rem;background:#fff;width:100%}

/* 평가요소 카드 */
.crit{border:1px solid #dde2e9;border-left:5px solid var(--c);border-radius:8px;background:#fff;overflow:hidden}
.crit.active{box-shadow:0 0 0 2px var(--ct),0 0 0 3px var(--c)}
.chd{display:flex;align-items:center;gap:.5rem;padding:.55rem .7rem .3rem;cursor:pointer;flex-wrap:wrap}
.chd:hover{background:#fbfcfd}
.cn{font-family:Consolas,monospace;font-size:.72rem;font-weight:700;color:#fff;background:var(--c);border-radius:3px;padding:0 .4rem;flex:none}
.cname{font-weight:600;font-size:.9rem;flex:1;min-width:8rem}
.chips{display:flex;gap:.3rem;flex-wrap:wrap}
.chip{font-size:.7rem;border:1px solid #dde2e9;border-radius:999px;padding:.05rem .5rem;color:#3d454e;background:#fff}
.chip.zero{color:#9e2f24;border-color:#f0c9c5;background:#fbeceb}
.chip.imp.HIGH{color:#9e2f24;border-color:#f0c9c5} .chip.imp.LOW{color:#6b747e} .chip.imp.MEDIUM{color:#3d454e}
.chip.clip{color:var(--c);border-color:var(--c);background:var(--ct)}
.chip.muted{color:#8b95a3}
.mv{display:flex;gap:.15rem} .mv button{border:1px solid #dde2e9;background:#fff;border-radius:4px;font-size:.7rem;padding:0 .35rem;cursor:pointer;color:#6b747e}
.mv button:disabled{opacity:.35;cursor:default}
.csay{padding:0 .7rem .55rem 2.2rem;font-size:.86rem;color:#153f47;line-height:1.55}
.csay >>> .q{font-weight:600;color:var(--c)}
.csay >>> .kw{font-weight:600;color:#1f5f6b}
.csay >>> .grp-say{background:#e6f0f6;border-radius:6px;padding:.05rem .4rem;box-decoration-break:clone;-webkit-box-decoration-break:clone}
.csay >>> .raw{font-size:.76rem;color:#3d454e}
.csay >>> .muted{color:#8b95a3;font-size:.8rem}

.cbd{border-top:1px solid #eaeef3;padding:.7rem .75rem .6rem;background:#fbfcfd;
  display:grid;grid-template-columns:minmax(0,1.5fr) minmax(15rem,1fr);gap:.9rem;align-items:start}
@media (max-width:56rem){.cbd{grid-template-columns:1fr}}
.col-main,.col-side{display:flex;flex-direction:column;gap:.65rem;min-width:0}
.col-side{border-left:1px solid #eaeef3;padding-left:.9rem}
@media (max-width:56rem){.col-side{border-left:0;padding-left:0;border-top:1px solid #eaeef3;padding-top:.6rem}}
.f.stack>span{display:flex;flex-direction:column;gap:.25rem}
.f.stack{gap:.55rem}
.hint{font-weight:400;color:#2f6b8f;font-size:.72rem}
.rawbox{border:1px solid #dde2e9;border-radius:6px;background:#fff;padding:.6rem .7rem;display:flex;flex-direction:column;gap:.5rem}
.rawsay{font-size:.88rem;color:#153f47;line-height:1.55}
.rawsay >>> .q{font-weight:600;color:var(--c)} .rawsay >>> .kw{font-weight:600;color:#1f5f6b}
.rawsay >>> .grp-say{background:#e6f0f6;border-radius:6px;padding:.05rem .4rem;box-decoration-break:clone;-webkit-box-decoration-break:clone}
.rawsay >>> .raw{font-family:Consolas,monospace;font-size:.76rem;color:#3d454e}
.rawacts{display:flex;align-items:center;gap:.6rem;flex-wrap:wrap}
.rawdet summary{font-size:.74rem;color:#6b747e;cursor:pointer}
.rawdet textarea{margin-top:.3rem}
.f{display:flex;flex-direction:column;gap:.25rem}
.f.two{flex-direction:row;gap:.6rem} .f.two>span{flex:1;display:flex;flex-direction:column;gap:.25rem}
.f.three{flex-direction:row;gap:.6rem;flex-wrap:wrap} .f.three>span{flex:1;min-width:9rem;display:flex;flex-direction:column;gap:.25rem}
.f label{font-size:.72rem;color:#6b747e;font-weight:500;display:flex;align-items:center;gap:.35rem}
.f input[type=text],.f input[type=number],.f select,.f textarea{border:1px solid #dde2e9;border-radius:5px;padding:.3rem .5rem;font:inherit;font-size:.86rem;background:#fff;width:100%}
.f input:focus,.f select:focus,.f textarea:focus{outline:2px solid #2f6b8f;outline-offset:-1px}
.rawf{font-size:.78rem;line-height:1.5;resize:vertical}
.lnk{border:0;background:none;color:#2f6b8f;text-decoration:underline;cursor:pointer;font:inherit;font-size:.76rem;padding:0}
.rule{font-size:.72rem;color:#6b747e;background:#f8f9fb;border:1px dashed #dde2e9;border-radius:5px;padding:.3rem .55rem;margin:.3rem 0 0}
.code{margin:.3rem 0 0;padding:.55rem .7rem;background:#1c2126;color:#d8dee5;border-radius:6px;font-size:.74rem;line-height:1.55;white-space:pre-wrap;overflow-wrap:anywhere}
.lvl{display:flex;align-items:center;gap:.3rem} .lvl select{flex:1} .slash{color:#8b95a3}
.zero{color:#9e2f24}
.cb{display:flex;align-items:center;gap:.4rem;font-size:.8rem;color:#3d454e;margin-top:.3rem;cursor:pointer}
.cb input{width:auto}

/* 증빙 위치 — 대상 고르기와 같은 상호작용, 다른 모양(클립) */
.evid{display:flex;align-items:center;gap:.45rem;min-height:2.1rem;border:1px dashed #dde2e9;border-radius:6px;padding:.32rem .6rem;font-size:.86rem;cursor:pointer;background:#fff;color:#6b747e}
.evid.picking{border-style:solid;border-color:var(--c);background:var(--ct);color:#153f47}
.evid.set{border-style:solid;border-color:var(--c);background:var(--ct);color:#153f47}
.evid .clipsw{font-size:.95rem}
.evid .where{font-size:.68rem;color:#6b747e;margin-left:auto}
.evid .x{border:0;background:none;color:#8b95a3;cursor:pointer;font-size:1rem;line-height:1;padding:0 .25rem;border-radius:3px}
.evid .x:hover{color:#fff;background:#a33228}

.acts{display:flex;gap:.4rem;justify-content:flex-end;padding-top:.2rem}
.addrow{display:flex}
.addc{border:1px dashed #dde2e9;background:none;border-radius:6px;padding:.4rem .9rem;font:inherit;font-size:.84rem;cursor:pointer;color:#2f6b8f}
.addc:hover{border-color:#2f6b8f;background:#e6f0f6}
.payload summary{font-size:.78rem;color:#6b747e;cursor:pointer}
.save{display:flex;gap:.5rem;align-items:center;justify-content:flex-end;border-top:1px solid #eaeef3;padding-top:.5rem}
.badge{display:inline-block;font-family:Consolas,monospace;font-size:.62rem;padding:.04rem .38rem;border-radius:3px;font-weight:600}
.badge.gen{background:#e6f0f6;color:#1f5f6b} .badge.manual{background:#fbf3e2;color:#8a6212} .badge.warn{background:#fbeceb;color:#9e2f24}
.btn{border:1px solid #dde2e9;background:#fff;border-radius:6px;padding:.35rem .8rem;font:inherit;cursor:pointer;color:#3d454e}
.btn.tiny{font-size:.78rem;padding:.25rem .6rem}
.btn.primary{background:#2f6b8f;border-color:#2f6b8f;color:#fff} .btn.primary:disabled{background:#dde2e9;border-color:#dde2e9;color:#8b95a3;cursor:not-allowed}
.btn.bad{color:#a33228;border-color:#f0c9c5} .btn.bad:hover{background:#fbeceb}
</style>
